import {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChevronDown,
  ChevronRight,
  Edit3,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../app/providers/AuthProvider";
import { FK } from "../../../capabilities/featureKeys";
import RoleDrawer from "./components/RoleDrawer";
import TeamAccessLayout, {
  accessButton,
  accessInput,
} from "./TeamAccessLayout";
import useSetupDrafts from "../../Scheduling/useSetupDrafts";
import {
  createBusinessRole,
  deleteBusinessRole,
  getBusinessRoles,
  getPermissionsCatalog,
  getRolePermissions,
  getTeamStaff,
  saveRolePermissions,
  updateBusinessRole,
} from "../../../api/accessControlApi";

function normalizeRole(r) {
  return {
    id: r?.id ?? r?.Id,
    name: r?.name ?? r?.Name ?? "",
    description: r?.description ?? r?.Description ?? "",
    isActive: r?.isActive ?? r?.IsActive ?? true,
  };
}

function normalizePermission(p) {
  const rawCode = p?.code ?? p?.Code ?? "";
  const code = String(rawCode || "")
    .trim()
    .toUpperCase();

  // Use permission Code as the stable key because backend role-permission mapping
  // is stored/replaced via PermissionCodes (not PermissionIds).
  const id =
    code ||
    String(
      p?.id ?? p?.Id ?? p?.permissionId ?? p?.PermissionId ?? rawCode ?? "",
    ).trim();

  return {
    id: id != null ? String(id) : "",
    code,
    name: p?.name ?? p?.Name ?? p?.displayName ?? p?.DisplayName ?? "",
    group: p?.group ?? p?.Group ?? "Other",
    description: p?.description ?? p?.Description ?? "",
  };
}

export function isPlatformOnlyPermission(permission) {
  const code = String(permission?.code ?? permission?.Code ?? "")
    .trim()
    .toUpperCase();
  const group = String(permission?.group ?? permission?.Group ?? "")
    .trim()
    .toLowerCase();

  return (
    code.startsWith("SUPER.ADMIN.") ||
    code.startsWith("PLATFORM.") ||
    group === "superadmin"
  );
}

export function filterBusinessAssignablePermissions(permissions) {
  return (permissions || []).filter(
    (permission) => !isPlatformOnlyPermission(permission),
  );
}

function extractArrayPayload(res) {
  const data = res?.data?.data ?? res?.data?.Data ?? res?.data ?? res;
  return Array.isArray(data) ? data : [];
}

function extractPermissionCatalogPayload(res) {
  const payload = res?.data?.data ?? res?.data?.Data ?? res?.data ?? res;

  if (Array.isArray(payload)) {
    const looksGrouped =
      payload.length > 0 &&
      typeof payload[0] === "object" &&
      payload[0] &&
      (Array.isArray(payload[0].Features) ||
        Array.isArray(payload[0].features));

    if (looksGrouped) {
      const flattened = [];
      payload.forEach((g) => {
        const groupName = g?.Group ?? g?.group ?? "Other";
        const features = g?.Features ?? g?.features ?? [];
        if (!Array.isArray(features)) return;
        features.forEach((p) => {
          flattened.push({ ...p, Group: p?.Group ?? p?.group ?? groupName });
        });
      });
      return flattened;
    }

    return payload;
  }

  return [];
}

function extractAssignedPermissionKeys(res) {
  const payload = res?.data?.data ?? res?.data?.Data ?? res?.data ?? res;
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload
      .map((x) => {
        if (typeof x === "string" || typeof x === "number") return String(x);
        if (x && typeof x === "object") {
          return String(x.id ?? x.Id ?? x.code ?? x.Code ?? "").trim();
        }
        return "";
      })
      .filter(Boolean);
  }

  const codes =
    payload.permissionCodes ??
    payload.PermissionCodes ??
    payload.codes ??
    payload.Codes ??
    [];
  if (Array.isArray(codes)) return codes.map((x) => String(x)).filter(Boolean);

  const ids =
    payload.permissionIds ?? payload.PermissionIds ?? payload.ids ?? [];
  if (Array.isArray(ids)) return ids.map((x) => String(x)).filter(Boolean);
  return [];
}

function areSetsEqual(left, right) {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

export default function AccessControlPage() {
  const drafts = useSetupDrafts({
    message:
      "Save the permission changes before leaving, or discard them to continue.",
  });
  const roleRequest = useRef(0);
  const savingRef = useRef(false);
  const roleSavingRef = useRef(false);
  const selectedRef = useRef("");
  const [rolesError, setRolesError] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [assignedError, setAssignedError] = useState(false);
  const [usersError, setUsersError] = useState(false);
  const { can, hasAllAccess, refreshEntitlements, refreshAuthContext } =
    useAuth();

  const canView =
    !!hasAllAccess ||
    (typeof can === "function" &&
      (can(FK.SETTINGS_ROLE_PERMISSIONS_MAPPING) || can("roles.view")));

  const canManage =
    !!hasAllAccess ||
    (typeof can === "function" &&
      (can(FK.SETTINGS_ROLE_PERMISSIONS_MAPPING) || can("roles.manage")));

  const canManagePermissions =
    !!hasAllAccess ||
    (typeof can === "function" &&
      (can(FK.SETTINGS_ROLE_PERMISSIONS_MAPPING) ||
        can("rolepermissions.manage")));

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [activeTab, setActiveTab] = useState("permissions");
  const [roleFilter, setRoleFilter] = useState("");

  const [roleFormMode, setRoleFormMode] = useState("create");
  const [roleForm, setRoleForm] = useState({
    id: "",
    name: "",
    description: "",
    isActive: true,
  });
  const [savingRole, setSavingRole] = useState(false);
  const [roleDrawerOpen, setRoleDrawerOpen] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [permissionFilter, setPermissionFilter] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [assignedPermissionKeys, setAssignedPermissionKeys] = useState(
    new Set(),
  );
  const [persistedPermissionKeys, setPersistedPermissionKeys] = useState(
    new Set(),
  );
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(true);

  const [teamStaff, setTeamStaff] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const deferredRoleFilter = useDeferredValue(roleFilter);
  const deferredPermissionFilter = useDeferredValue(permissionFilter);

  const selectedRole = useMemo(() => {
    const found = (roles || []).find(
      (r) => String(r.id) === String(selectedRoleId),
    );
    return found || null;
  }, [roles, selectedRoleId]);

  const assignedUsers = useMemo(
    () =>
      (teamStaff || []).filter(
        (user) => String(user.roleId || "") === String(selectedRoleId),
      ),
    [selectedRoleId, teamStaff],
  );

  const roleUserCounts = useMemo(() => {
    const counts = new Map();
    (teamStaff || []).forEach((user) => {
      const key = String(user.roleId || "").trim();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [teamStaff]);

  const filteredRoles = useMemo(() => {
    const query = String(deferredRoleFilter || "")
      .trim()
      .toLowerCase();
    if (!query) return roles;

    return roles.filter((role) => {
      const haystack = [
        role.name,
        role.description,
        role.isActive ? "active" : "inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [deferredRoleFilter, roles]);

  const grouped = useMemo(() => {
    const query = String(deferredPermissionFilter || "")
      .trim()
      .toLowerCase();

    const filtered = (catalog || []).filter((permission) => {
      if (!query) return true;
      return (
        String(permission.name || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.code || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.group || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.description || "")
          .toLowerCase()
          .includes(query)
      );
    });

    const byGroup = new Map();
    filtered.forEach((permission) => {
      const groupName = permission.group || "Other";
      if (!byGroup.has(groupName)) byGroup.set(groupName, []);
      byGroup.get(groupName).push(permission);
    });

    return Array.from(byGroup.entries())
      .map(([name, permissions]) => ({
        name,
        permissions: permissions.sort((a, b) =>
          String(a.name || a.code || "").localeCompare(
            String(b.name || b.code || ""),
          ),
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [catalog, deferredPermissionFilter]);

  const permissionChangesPending = useMemo(
    () => !areSetsEqual(assignedPermissionKeys, persistedPermissionKeys),
    [assignedPermissionKeys, persistedPermissionKeys],
  );

  const visiblePermissionIds = useMemo(
    () =>
      grouped.flatMap((group) =>
        group.permissions.map((permission) => permission.id),
      ),
    [grouped],
  );

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true);
    setRolesError(false);
    try {
      const res = await getBusinessRoles();
      const list = extractArrayPayload(res)
        .map(normalizeRole)
        .filter((r) => r.id);
      setRoles(list);
      setSelectedRoleId((currentId) => {
        const currentExists = list.some(
          (role) => String(role.id) === String(currentId),
        );
        if (currentExists) return String(currentId);
        return list.length > 0 ? String(list[0].id) : "";
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setRolesError(true);
      toast.error("Failed to load roles");
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(false);
    try {
      const res = await getPermissionsCatalog();
      const list = extractPermissionCatalogPayload(res)
        .map(normalizePermission)
        .filter((permission) => permission.id && permission.code)
        .filter((permission) => !isPlatformOnlyPermission(permission));
      setCatalog(list);
      setExpandedGroups(new Set());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setCatalogError(true);
      toast.error("Failed to load permissions catalog");
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const loadRolePermissions = useCallback(async (roleId) => {
    const requestId = ++roleRequest.current;
    setAssignedError(false);
    setLoadingAssigned(true);
    setAssignedPermissionKeys(new Set());
    setPersistedPermissionKeys(new Set());
    try {
      const res = await getRolePermissions(roleId);
      if (requestId !== roleRequest.current || selectedRef.current !== roleId)
        return;
      const keys = new Set(
        extractAssignedPermissionKeys(res).map((key) =>
          key.trim().toUpperCase(),
        ),
      );
      setAssignedPermissionKeys(keys);
      setPersistedPermissionKeys(keys);
    } catch {
      if (requestId === roleRequest.current) setAssignedError(true);
    } finally {
      if (requestId === roleRequest.current) setLoadingAssigned(false);
    }
  }, []);

  const loadTeamStaff = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(false);
    try {
      const res = await getTeamStaff();
      const list = extractArrayPayload(res);
      const normalized = list.map((user) => ({
        id: user?.id ?? user?.Id,
        name: user?.name ?? user?.Name ?? "",
        email: user?.email ?? user?.Email ?? "",
        status: user?.status ?? user?.Status ?? "",
        roleId: user?.roleId ?? user?.RoleId,
        roleName: user?.roleName ?? user?.RoleName,
        createdAt: user?.createdAt ?? user?.CreatedAt,
      }));
      setTeamStaff(normalized);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setUsersError(true);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    loadRoles();
    loadCatalog();
    loadTeamStaff();
  }, [canView, loadCatalog, loadRoles, loadTeamStaff]);

  useEffect(() => {
    selectedRef.current = selectedRoleId;
    if (canView && selectedRoleId) loadRolePermissions(selectedRoleId);
    return () => {
      roleRequest.current += 1;
    };
  }, [canView, loadRolePermissions, selectedRoleId, drafts.reset]);
  const updateDraft = drafts.context.update;
  useLayoutEffect(() => {
    updateDraft("permissions", permissionChangesPending);
    return () => updateDraft("permissions", false);
  }, [updateDraft, permissionChangesPending]);
  const chooseRole = (roleId) => {
    if (savingRef.current || roleId === selectedRoleId) return;
    drafts.guard(() => {
      selectedRef.current = roleId;
      roleRequest.current += 1;
      setLoadingAssigned(true);
      setSelectedRoleId(roleId);
    });
  };

  useEffect(() => {
    if (!selectedRole) return;
    setRoleFormMode("edit");
    setRoleForm({
      id: String(selectedRole.id),
      name: selectedRole.name || "",
      description: selectedRole.description || "",
      isActive: !!selectedRole.isActive,
    });
  }, [selectedRole]);

  const openCreateRole = () => {
    setRoleFormMode("create");
    setRoleForm({
      id: "",
      name: "",
      description: "",
      isActive: true,
    });
    setRoleDrawerOpen(true);
  };

  const openEditRole = (roleToEdit) => {
    const targetRole = roleToEdit || selectedRole;
    if (!targetRole) return;
    setRoleFormMode("edit");
    setRoleForm({
      id: String(targetRole.id),
      name: targetRole.name || "",
      description: targetRole.description || "",
      isActive: !!targetRole.isActive,
    });
    setRoleDrawerOpen(true);
  };

  const saveRole = async () => {
    if (!canManage || roleSavingRef.current) return;

    const name = String(roleForm.name || "").trim();
    if (!name) {
      toast.warn("Role name is required");
      return;
    }

    roleSavingRef.current = true;
    setSavingRole(true);
    try {
      if (roleFormMode === "create") {
        const res = await createBusinessRole({
          name,
          description: String(roleForm.description || "").trim(),
          isActive: !!roleForm.isActive,
        });
        toast.success("Role created");

        await loadRoles();
        await loadTeamStaff();

        const created = res?.data?.data ?? res?.data;
        const createdId = created?.id ?? created?.Id;
        if (createdId) {
          setSelectedRoleId(String(createdId));
          setActiveTab("permissions");
        }
      } else {
        await updateBusinessRole(roleForm.id, {
          name,
          description: String(roleForm.description || "").trim(),
          isActive: !!roleForm.isActive,
        });
        toast.success("Role updated");
        await loadRoles();
      }

      setRoleDrawerOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to save role");
    } finally {
      roleSavingRef.current = false;
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleToDelete) => {
    const targetRole = roleToDelete || selectedRole;
    if (!canManage || !targetRole || savingRef.current) return;

    const confirmed = window.confirm(
      `Delete role "${targetRole.name}"? This will deactivate or remove it depending on server policy.`,
    );
    if (!confirmed) return;

    try {
      await deleteBusinessRole(targetRole.id);
      toast.success("Role deleted");
      if (String(targetRole.id) === String(selectedRoleId)) {
        setSelectedRoleId("");
      }
      await loadRoles();
      await loadTeamStaff();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to delete role");
    }
  };

  const toggleGroupExpanded = (groupName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const editable = Boolean(
    selectedRole &&
      canManagePermissions &&
      !loadingAssigned &&
      !catalogLoading &&
      !assignedError &&
      !catalogError &&
      !savingPermissions,
  );
  const setPermissionScope = (permissionIds, enabled) => {
    if (!editable || savingRef.current) return;
    setAssignedPermissionKeys((prev) => {
      const next = new Set(prev);
      permissionIds.forEach((permissionId) => {
        if (!permissionId) return;
        if (enabled) next.add(String(permissionId));
        else next.delete(String(permissionId));
      });
      return next;
    });
  };

  const togglePermission = (permissionId, enabled) => {
    if (!editable || savingRef.current) return;
    setAssignedPermissionKeys((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(String(permissionId));
      else next.delete(String(permissionId));
      return next;
    });
  };

  const savePermissions = async () => {
    if (!editable || !permissionChangesPending || savingRef.current) return;
    savingRef.current = true;
    setSavingPermissions(true);
    const roleId = selectedRoleId;
    const permissionCodes = Array.from(assignedPermissionKeys).filter(
      (code) =>
        !isPlatformOnlyPermission(
          catalog.find((permission) => permission.id === code) || { code },
        ),
    );
    try {
      await saveRolePermissions(roleId, { permissionCodes });
      if (selectedRef.current === roleId) {
        setPersistedPermissionKeys(new Set(permissionCodes));
        setAssignedPermissionKeys(new Set(permissionCodes));
        drafts.saved();
      }
      toast.success("Permissions saved");
      // A refresh failure must not misreport a successful permission replacement.
      await Promise.allSettled([
        Promise.resolve().then(() => refreshEntitlements?.()),
        Promise.resolve().then(() => refreshAuthContext?.()),
      ]);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Failed to save permissions. Your changes have been kept.",
      );
    } finally {
      savingRef.current = false;
      setSavingPermissions(false);
    }
  };

  if (!canView)
    return (
      <TeamAccessLayout>
        <p role="alert">You do not have access to roles and permissions.</p>
      </TeamAccessLayout>
    );
  return (
    <TeamAccessLayout>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Roles & permissions</h2>
          <p className="mt-1 text-xs text-slate-500">
            Define access once, then assign the role to team members.
          </p>
        </div>
        {canManage && (
          <button
            className={`${accessButton} !border-emerald-600 !text-emerald-700`}
            disabled={savingPermissions}
            onClick={() => drafts.guard(openCreateRole)}
          >
            <Plus size={16} />
            New role
          </button>
        )}
      </div>
      <div className="grid items-start gap-4 grid-cols-[260px_minmax(0,1fr)]">
        <aside
          aria-label="Role directory"
          className="sticky top-4 overflow-hidden rounded-lg border border-slate-200 bg-white"
        >
          <div className="border-b border-slate-200 bg-slate-50 p-3">
            <h3 className="font-medium">
              Roles{" "}
              <span className="text-xs text-slate-500">({roles.length})</span>
            </h3>
            <input
              aria-label="Search roles"
              placeholder="Search roles"
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              className={`${accessInput} mt-3 w-full`}
            />
          </div>
          <div className="max-h-[65vh] overflow-auto p-2">
            {loadingRoles ? (
              <p role="status" className="p-3 text-slate-500">
                Loading roles...
              </p>
            ) : rolesError ? (
              <p role="alert" className="p-3">
                Roles could not be loaded.{" "}
                <button className="underline" onClick={loadRoles}>
                  Retry roles
                </button>
              </p>
            ) : filteredRoles.length ? (
              filteredRoles.map((role) => (
                <button
                  key={role.id}
                  disabled={savingPermissions}
                  aria-pressed={String(role.id) === selectedRoleId}
                  onClick={() => chooseRole(String(role.id))}
                  className={`mb-1 w-full rounded-md px-3 py-3 text-left ${String(role.id) === selectedRoleId ? "bg-emerald-50 text-emerald-800" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  <span className="block font-medium">{role.name}</span>
                  <span className="mt-1 block text-xs text-slate-500">
                    {role.isActive ? "Active" : "Inactive"} /{" "}
                    {usersError
                      ? "Member count unavailable"
                      : `${roleUserCounts.get(String(role.id)) || 0} members`}
                  </span>
                </button>
              ))
            ) : (
              <p className="p-3 text-slate-500">
                {roles.length
                  ? "No roles match this search."
                  : "No roles created yet."}
              </p>
            )}
          </div>
        </aside>
        <section
          aria-label="Selected role"
          className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white"
        >
          {!selectedRole ? (
            <div className="p-10 text-center">
              <h3 className="text-base font-medium">
                {rolesError
                  ? "Roles are unavailable"
                  : roles.length
                    ? "Select a role"
                    : "Create your first role"}
              </h3>
              <p className="mt-2 text-slate-500">
                {rolesError
                  ? "Retry loading the role directory to continue."
                  : "Choose which parts of XploreByte your team can access."}
              </p>
              {canManage && !rolesError && !loadingRoles && (
                <button
                  className={`${accessButton} mt-4 !border-emerald-600 !text-emerald-700`}
                  onClick={openCreateRole}
                >
                  <Plus size={16} />
                  Create role
                </button>
              )}
            </div>
          ) : (
            <>
              <header className="border-b border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold">
                      {selectedRole.name}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {selectedRole.isActive ? "Active" : "Inactive"} /{" "}
                      {assignedPermissionKeys.size} permissions
                      {usersError ? "" : ` / ${assignedUsers.length} members`}
                    </p>
                    {selectedRole.description && (
                      <p className="mt-2 text-slate-600">
                        {selectedRole.description}
                      </p>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex gap-2">
                      <button
                        className={accessButton}
                        disabled={savingPermissions}
                        onClick={() =>
                          drafts.guard(() => openEditRole(selectedRole))
                        }
                      >
                        <Edit3 size={14} />
                        Edit role
                      </button>
                      <button
                        aria-label={`Delete ${selectedRole.name}`}
                        title="Delete role"
                        className={`${accessButton} w-9 !px-0 !text-rose-700`}
                        disabled={savingPermissions}
                        onClick={() =>
                          drafts.guard(() => handleDeleteRole(selectedRole))
                        }
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>
              </header>
              <div
                className="flex gap-5 border-b border-slate-200 px-4"
                aria-label="Role details"
              >
                {[
                  ["permissions", "Permissions"],
                  ["users", "Assigned members"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    aria-pressed={activeTab === id}
                    className={`border-b-2 py-3 ${activeTab === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"}`}
                    onClick={() => setActiveTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {activeTab === "users" ? (
                <div className="p-4">
                  {usersLoading ? (
                    <p role="status">Loading assigned members...</p>
                  ) : usersError ? (
                    <p role="alert">
                      Assigned members could not be loaded.{" "}
                      <button className="underline" onClick={loadTeamStaff}>
                        Retry members
                      </button>
                    </p>
                  ) : assignedUsers.length ? (
                    <table
                      aria-label="Assigned members"
                      className="w-full text-left"
                    >
                      <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                          {["Name", "Email", "Status"].map((label) => (
                            <th
                              key={label}
                              scope="col"
                              className="px-3 py-2 font-medium"
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {assignedUsers.map((member) => (
                          <tr
                            key={member.id}
                            className="border-t border-slate-100"
                          >
                            <td className="px-3 py-3">{member.name}</td>
                            <td className="px-3 py-3">{member.email}</td>
                            <td className="px-3 py-3">{member.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="py-6 text-center text-slate-500">
                      No team members are assigned to this role.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="sticky top-0 z-10 space-y-3 border-b border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <input
                        aria-label="Search permissions"
                        placeholder="Search permissions or modules"
                        value={permissionFilter}
                        onChange={(event) =>
                          setPermissionFilter(event.target.value)
                        }
                        className={`${accessInput} w-64`}
                      />
                      <div className="flex items-center gap-2">
                        {permissionChangesPending && (
                          <span
                            role="status"
                            className="text-xs text-amber-700"
                          >
                            Unsaved changes
                          </span>
                        )}
                        <button
                          className={`${accessButton} !border-emerald-600 !text-emerald-700`}
                          onClick={savePermissions}
                          disabled={!editable || !permissionChangesPending}
                        >
                          <Save size={15} />
                          {savingPermissions ? "Saving..." : "Save changes"}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>
                        Bulk actions apply to search results, including
                        collapsed groups.
                      </span>
                      <button
                        className="text-slate-700 underline disabled:opacity-50"
                        disabled={!editable || !visiblePermissionIds.length}
                        onClick={() =>
                          setPermissionScope(visiblePermissionIds, true)
                        }
                      >
                        Select visible
                      </button>
                      <button
                        className="text-slate-700 underline disabled:opacity-50"
                        disabled={!editable || !visiblePermissionIds.length}
                        onClick={() =>
                          setPermissionScope(visiblePermissionIds, false)
                        }
                      >
                        Clear visible
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[65vh] overflow-y-auto">
                    {catalogLoading || loadingAssigned ? (
                      <p role="status" className="p-6 text-slate-500">
                        Loading permissions...
                      </p>
                    ) : assignedError || catalogError ? (
                      <p role="alert" className="p-6">
                        Permissions could not be loaded. Saving is disabled to
                        protect the current mapping.{" "}
                        <button
                          className="underline"
                          onClick={() => {
                            if (catalogError) loadCatalog();
                            if (assignedError)
                              loadRolePermissions(selectedRoleId);
                          }}
                        >
                          Retry permissions
                        </button>
                      </p>
                    ) : !grouped.length ? (
                      <p className="p-6 text-slate-500">
                        No permissions match this search.
                      </p>
                    ) : (
                      grouped.map((group) => {
                        const expanded =
                          expandedGroups.has(group.name) ||
                          Boolean(permissionFilter.trim());
                        const enabled = group.permissions.filter((permission) =>
                          assignedPermissionKeys.has(permission.id),
                        ).length;
                        return (
                          <section
                            key={group.name}
                            className="border-b border-slate-200 last:border-0"
                          >
                            <div className="flex items-center justify-between gap-3 bg-slate-50/60 px-4 py-3">
                              <button
                                className="flex min-w-0 items-center gap-2 text-left"
                                aria-expanded={expanded}
                                onClick={() => toggleGroupExpanded(group.name)}
                              >
                                {expanded ? (
                                  <ChevronDown size={15} />
                                ) : (
                                  <ChevronRight size={15} />
                                )}
                                <span className="font-medium">
                                  {group.name}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {enabled} of {group.permissions.length}
                                </span>
                              </button>
                              <div className="flex gap-3 text-xs">
                                <button
                                  disabled={!editable}
                                  onClick={() =>
                                    setPermissionScope(
                                      group.permissions.map(
                                        (permission) => permission.id,
                                      ),
                                      true,
                                    )
                                  }
                                  className="text-slate-600 disabled:opacity-50"
                                >
                                  Select all
                                </button>
                                <button
                                  disabled={!editable}
                                  onClick={() =>
                                    setPermissionScope(
                                      group.permissions.map(
                                        (permission) => permission.id,
                                      ),
                                      false,
                                    )
                                  }
                                  className="text-slate-600 disabled:opacity-50"
                                >
                                  Clear
                                </button>
                              </div>
                            </div>
                            {expanded && (
                              <div className="divide-y divide-slate-100">
                                {group.permissions.map((permission) => (
                                  <label
                                    key={permission.id}
                                    className="flex cursor-pointer items-start gap-3 px-5 py-3 hover:bg-slate-50"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={assignedPermissionKeys.has(
                                        permission.id,
                                      )}
                                      disabled={!editable}
                                      onChange={(event) =>
                                        togglePermission(
                                          permission.id,
                                          event.target.checked,
                                        )
                                      }
                                      className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-emerald-600"
                                    />
                                    <span className="min-w-0">
                                      <span className="block text-slate-800">
                                        {permission.name || permission.code}
                                      </span>
                                      {permission.description && (
                                        <span className="mt-0.5 block text-xs text-slate-500">
                                          {permission.description}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </section>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>
      <RoleDrawer
        open={roleDrawerOpen}
        mode={roleFormMode}
        value={roleForm}
        onChange={setRoleForm}
        onClose={() => {
          if (!savingRole) setRoleDrawerOpen(false);
        }}
        onSave={saveRole}
        saving={savingRole}
        canManage={canManage}
      />
      {drafts.dialog}
    </TeamAccessLayout>
  );
}
